import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';

import { MatDialog } from '@angular/material/dialog';
import { Daypart, DaypartObj, User } from 'src/app/interfaces';
import {
  arrayUnion,
  arrayRemove,
  collection,
  where,
  query,
  getDocs,
  getDoc,
  doc,
  onSnapshot,
  setDoc,
  Unsubscribe,
} from 'firebase/firestore';
import { ConfirmChangeAvailabilityComponent } from '../../dialogs/confirm-change-availability/confirm-change-availability.component';

import moment from 'moment';
import { ucFirst } from 'src/app/globals';
import { dataStore, db, functions } from 'src/app/app.component';
import { httpsCallable } from 'firebase/functions';
import { Role } from 'src/app/enums';
import { MatCard, MatCardContent } from '@angular/material/card';
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll } from '@angular/cdk/scrolling';
import { MatSlideToggle } from '@angular/material/slide-toggle';

interface DayPartObjWithChecked extends DaypartObj {
  checked?: boolean;
}

@Component({
  selector: 'app-availability',
  templateUrl: './availability.component.html',
  styleUrls: ['./availability.component.scss'],
  imports: [
    MatCard,
    MatCardContent,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    MatSlideToggle,
  ],
})
export class AvailabilityComponent implements OnInit, OnDestroy {
  newDaypartsDriver = signal<DayPartObjWithChecked[]>([]);
  newDaypartsPlanner = signal<DayPartObjWithChecked[]>([]);
  organisationId = dataStore.selectedOrganisation.id;
  userId: string | null = null;
  loading = signal(true);
  userIdFromAdmin: string | null = null;
  changerName: string | null = null;
  private daypartsDriverUnsub?: Unsubscribe;
  private daypartsPlannerUnsub?: Unsubscribe;
  moment = moment;
  ucFirst = ucFirst;
  RoleEnum = Role;

  @Input() set getUserId(userId: string) {
    this.userIdFromAdmin = userId ? userId : null;
    this.getDayparts();
  }
  @Input() userCanModify: boolean | null = null;
  @Input() type: string | null = null;

  constructor(private dialog: MatDialog) {}

  ngOnInit(): void {
    this.userId = this.userIdFromAdmin ? this.userIdFromAdmin : dataStore.rootUser.id!;
    if (this.userId) {
      this.getDayparts();
    }
  }

  ngOnDestroy(): void {
    this.daypartsDriverUnsub?.();
    this.daypartsPlannerUnsub?.();
  }
  createDaypartObj(oldDaypart: any) {
    const daypart = oldDaypart.daypart;
    let daypartObj = { day: daypart.day } as any;
    switch (daypart.day) {
      case 0:
        daypartObj.dayString = 'Maandag';
        break;
      case 1:
        daypartObj.dayString = 'Dinsdag';
        break;
      case 2:
        daypartObj.dayString = 'Woensdag';
        break;
      case 3:
        daypartObj.dayString = 'Donderdag';
        break;
      case 4:
        daypartObj.dayString = 'Vrijdag';
        break;
      case 5:
        daypartObj.dayString = 'Zaterdag';
        break;
      case 6:
        daypartObj.dayString = 'Zondag';
        break;
    }

    // format time
    daypartObj.startHour =
      daypart.startHour.toString().length !== 1
        ? daypart.startHour
        : '0' + daypart.startHour.toString();
    daypartObj.endHour =
      daypart.endHour.toString().length !== 1 ? daypart.endHour : '0' + daypart.endHour.toString();
    daypartObj.endMin =
      daypart.endMin.toString().length !== 1 ? daypart.endMin : daypart.endMin.toString() + '0';
    daypartObj.startMin =
      daypart.startMin.toString().length !== 1
        ? daypart.startMin
        : daypart.startMin.toString() + '0';

    daypartObj.id = oldDaypart.id;
    daypartObj.name = daypart.name;

    if (oldDaypart.availableUsers) {
      oldDaypart.availableUsers.forEach((userRef: any) => {
        const userId = this.userIdFromAdmin ? this.userIdFromAdmin : this.userId;
        if (userRef.path.includes(userId!)) {
          daypartObj.checked = true;
        }
      });
    }

    return daypartObj;
  }

  async getDayparts() {
    this.daypartsDriverUnsub?.();
    this.daypartsPlannerUnsub?.();

    this.daypartsDriverUnsub = onSnapshot(
      query(
        collection(db, `organisations/${this.organisationId}/dayparts`),
        where('type', '==', 'driver'),
      ),
      (snapshot) => {
        const newDayparts: any[] = [];
        snapshot.forEach((doc) => {
          let daypartObj = this.createDaypartObj({ ...doc.data(), id: doc.id });

          newDayparts.push(daypartObj);
        });
        this.newDaypartsDriver.set(
          newDayparts.sort((a, b) => {
            return (
              a.day - b.day ||
              Number(a.startHour) - Number(b.startHour) ||
              Number(a.startMin) - Number(b.startMin) ||
              Number(a.endHour) - Number(b.endHour) ||
              Number(a.endMin) - Number(b.endMin)
            );
          }),
        );
        this.loading.set(false);
      },
    );

    this.daypartsPlannerUnsub = onSnapshot(
      query(
        collection(db, `organisations/${this.organisationId}/dayparts`),
        where('type', '==', 'planner'),
      ),
      (snapshot) => {
        const newDayparts: any[] = [];
        snapshot.forEach((doc) => {
          let daypartObj = this.createDaypartObj({ ...doc.data(), id: doc.id });

          newDayparts.push(daypartObj);
        });
        this.newDaypartsPlanner.set(
          newDayparts.sort((a, b) => {
            return (
              a.day - b.day ||
              Number(a.startHour) - Number(b.startHour) ||
              Number(a.startMin) - Number(b.startMin) ||
              Number(a.endHour) - Number(b.endHour) ||
              Number(a.endMin) - Number(b.endMin)
            );
          }),
        );
        this.loading.set(false);
      },
    );
  }

  async toggleChange($event: any, daypartId: string) {
    const userId = this.userIdFromAdmin ? this.userIdFromAdmin : this.userId;
    console.log('userId', userId);
    const userRef = doc(db, `organisations/${this.organisationId}/users/${userId}`);
    console.log('event', $event);
    if ($event.checked) {
      await setDoc(
        doc(db, `organisations/${this.organisationId}/dayparts/${daypartId}`),
        {
          availableUsers: arrayUnion(userRef),
        },
        { merge: true },
      );
    } else {
      const today = new Date();
      let filteredDaypartDays = [];

      if (this.type === Role.Driver || this.type === Role.CoDriver) {
        const fieldName = this.type === Role.Driver ? 'driverId' : 'coDriverId';
        const daypartDays = await getDocs(
          query(
            collection(db, `organisations/${this.organisationId}/daypartDays`),
            where('year', '>=', today.getFullYear()),
            where('daypartId', '==', daypartId),
            where(fieldName, '==', userId),
          ),
        );
        if (!daypartDays.empty) {
          daypartDays.docs.forEach((daypartDay) => {
            const data = daypartDay.data();
            if (moment([data.year, data.month, data.day]).isSameOrAfter(moment(), 'day')) {
              filteredDaypartDays.push(daypartDay);
            }
          });
        }
      } else if (this.type === Role.Planner) {
        const daypartDays = await getDocs(
          query(
            collection(db, `organisations/${this.organisationId}/daypartDays`),
            where('year', '>=', today.getFullYear()),
            where('daypartId', '==', daypartId),
            where('planners', 'array-contains', userId),
          ),
        );
        if (!daypartDays.empty) {
          daypartDays.docs.forEach((daypartDay) => {
            const data = daypartDay.data();
            if (moment([data.year, data.month, data.day]).isSameOrAfter(moment(), 'day')) {
              filteredDaypartDays.push(daypartDay);
            }
          });
        }
      }

      if (filteredDaypartDays.length > 0) {
        const daypart = [...this.newDaypartsDriver(), ...this.newDaypartsPlanner()].find(
          (dp) => dp.id === daypartId,
        );
        const dialogRef = this.dialog.open(ConfirmChangeAvailabilityComponent, {
          width: '500px',
          disableClose: true,
          data: {
            availabilityType: 'availability',
            itemName: daypart ? daypart.name : '',
          },
        });
        dialogRef.afterClosed().subscribe(async (result) => {
          if (result) {
            const newUser = (
              await getDoc(doc(db, `organisations/${this.organisationId}/users/`, `${userId}`))
            ).data() as User;
            this.changerName = newUser.name ?? '';
            await setDoc(
              doc(db, `organisations/${this.organisationId}/dayparts/${daypartId}`),
              {
                availableUsers: arrayRemove(userRef),
              },
              { merge: true },
            );

            if (this.type === Role.Driver || this.type === Role.CoDriver) {
              try {
                const callBody = {
                  orgId: this.organisationId,
                  userName: 'planner',
                  mailType: 'scheduleAvailability',
                  emailTo: 'self',
                  optionalData: {
                    date: new Date(),
                    changedBy: this.changerName,
                    userChanged: newUser.name,
                    userRole: newUser.roles?.includes(Role.Driver) ? 'chauffeur' : 'bijrijder',
                    mailPlanners: true,
                  },
                };
                const callable = httpsCallable(functions, 'httpSendMail');
                const result = await callable(callBody);
                console.log(result);
              } catch (error) {
                console.log(error);
              }
            }
          } else {
            $event.source.checked = true;
          }
        });
      } else {
        await setDoc(
          doc(db, `organisations/${this.organisationId}/dayparts/${daypartId}`),
          {
            availableUsers: arrayRemove(userRef),
          },
          { merge: true },
        );
      }
    }
  }

  get getUserId() {
    return this.userIdFromAdmin!;
  }
}
